import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import { restError } from "@/models/client/RestResponse";
import {
  Dashboard,
  WidgetTemplate,
  CreateDashboardRequest,
  UpdateDashboardRequest,
  PublishDashboardRequest,
  ShareDashboardRequest,
  CloneDashboardAsNewRequest,
  CreateWidgetTemplateRequest,
  UpdateWidgetTemplateRequest,
  parseDashboardResponse,
  parseWidgetTemplateResponse,
  serializeDashboardConfig,
  DashboardConfig,
} from "@/models/dashboard/Dashboard";

const DASHBOARD_KEY = PATH_KEYS.dashboard;

/**
 * Puts a dashboard the user just created into the cached list before the mutation resolves. The
 * dashboard page selects it straight away, and a list without it trips the page's "selected
 * dashboard is gone" fallback, which jumps back to the default dashboard (LAR-661).
 */
const addToDashboardList = (
  queryClient: QueryClient,
  dashboard: Dashboard
): void => {
  queryClient.setQueryData<Dashboard[]>([DASHBOARD_KEY], (current) =>
    current && !current.some((d) => d.id === dashboard.id)
      ? [...current, dashboard]
      : current
  );
};

// ===== Dashboard Hooks =====

export const useDashboards = () => {
  const dashboardService = useItemService((services) => services.dashboard);

  return useQuery<Dashboard[], Error>({
    queryKey: [DASHBOARD_KEY],
    queryFn: async () => {
      const response = await dashboardService.getDashboards();
      if (response.success) {
        return response.value.map(parseDashboardResponse);
      }
      throw restError(response);
    },
  });
};

export const useDashboard = (id: string | null) => {
  const dashboardService = useItemService((services) => services.dashboard);

  return useQuery<Dashboard | null, Error>({
    queryKey: [DASHBOARD_KEY, id],
    queryFn: async () => {
      if (!id) return null;
      const response = await dashboardService.getDashboard(id);
      if (response.success) {
        return parseDashboardResponse(response.value);
      }
      throw restError(response);
    },
    enabled: !!id,
  });
};

export const useCreateDashboard = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<Dashboard, Error, CreateDashboardRequest>({
    mutationKey: [DASHBOARD_KEY, "create"],
    mutationFn: async (request) => {
      const response = await dashboardService.createDashboard(request);
      if (response.success) {
        return parseDashboardResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: (created) => {
      addToDashboardList(queryClient, created);
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
    },
  });
};

export const useUpdateDashboard = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<
    Dashboard,
    Error,
    { id: string; request: UpdateDashboardRequest }
  >({
    mutationKey: [DASHBOARD_KEY, "update"],
    mutationFn: async ({ id, request }) => {
      const response = await dashboardService.updateDashboard(id, request);
      if (response.success) {
        return parseDashboardResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
      queryClient.setQueryData([DASHBOARD_KEY, data.id], data);
    },
  });
};

export const useDeleteDashboard = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: [DASHBOARD_KEY, "delete"],
    mutationFn: async (id) => {
      const response = await dashboardService.deleteDashboard(id);
      if (!response.success) {
        throw restError(response);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
    },
  });
};

export const useSetDefaultDashboard = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: [DASHBOARD_KEY, "setDefault"],
    mutationFn: async (id) => {
      const response = await dashboardService.setDefaultDashboard(id);
      if (!response.success) {
        throw restError(response);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
    },
  });
};

/**
 * Publishes an organization dashboard to the tenant it belongs to, or turns it back into a draft
 * (LAR-93). Publishing snapshots the configuration as it stands, so later edits stay invisible
 * until it is published again.
 */
export const usePublishDashboard = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<
    Dashboard,
    Error,
    { id: string; request: PublishDashboardRequest }
  >({
    mutationKey: [DASHBOARD_KEY, "publish"],
    mutationFn: async ({ id, request }) => {
      const response = await dashboardService.publishDashboard(id, request);
      if (response.success) {
        return parseDashboardResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
      queryClient.setQueryData([DASHBOARD_KEY, data.id], data);
    },
  });
};

/**
 * Releases a published organization dashboard to the tenant's sub-tenants, or withdraws it again
 * (LAR-93).
 */
export const useShareDashboardWithSubTenants = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<
    Dashboard,
    Error,
    { id: string; request: ShareDashboardRequest }
  >({
    mutationKey: [DASHBOARD_KEY, "share"],
    mutationFn: async ({ id, request }) => {
      const response = await dashboardService.shareDashboardWithSubTenants(
        id,
        request
      );
      if (response.success) {
        return parseDashboardResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
      queryClient.setQueryData([DASHBOARD_KEY, data.id], data);
    },
  });
};

export const useCloneDashboard = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<Dashboard, Error, string>({
    mutationKey: [DASHBOARD_KEY, "clone"],
    mutationFn: async (id) => {
      const response = await dashboardService.cloneDashboard(id);
      if (response.success) {
        return parseDashboardResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
    },
  });
};

export const useCloneDashboardAsNew = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<Dashboard, Error, { id: string; request: CloneDashboardAsNewRequest }>({
    mutationKey: [DASHBOARD_KEY, "cloneAsNew"],
    mutationFn: async ({ id, request }) => {
      const response = await dashboardService.cloneDashboardAsNew(id, request);
      if (response.success) {
        return parseDashboardResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: (created) => {
      addToDashboardList(queryClient, created);
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_KEY] });
    },
  });
};

// ===== Widget Template Hooks =====

export const useWidgetTemplates = () => {
  const dashboardService = useItemService((services) => services.dashboard);

  return useQuery<WidgetTemplate[], Error>({
    queryKey: [DASHBOARD_KEY, "templates"],
    queryFn: async () => {
      const response = await dashboardService.getTemplates();
      if (response.success) {
        return response.value.map(parseWidgetTemplateResponse);
      }
      throw restError(response);
    },
  });
};

export const useWidgetTemplate = (id: string | null) => {
  const dashboardService = useItemService((services) => services.dashboard);

  return useQuery<WidgetTemplate | null, Error>({
    queryKey: [DASHBOARD_KEY, "templates", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await dashboardService.getTemplate(id);
      if (response.success) {
        return parseWidgetTemplateResponse(response.value);
      }
      throw restError(response);
    },
    enabled: !!id,
  });
};

export const useCreateWidgetTemplate = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<WidgetTemplate, Error, CreateWidgetTemplateRequest>({
    mutationKey: [DASHBOARD_KEY, "templates", "create"],
    mutationFn: async (request) => {
      const response = await dashboardService.createTemplate(request);
      if (response.success) {
        return parseWidgetTemplateResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DASHBOARD_KEY, "templates"],
      });
    },
  });
};

export const useUpdateWidgetTemplate = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<
    WidgetTemplate,
    Error,
    { id: string; request: UpdateWidgetTemplateRequest }
  >({
    mutationKey: [DASHBOARD_KEY, "templates", "update"],
    mutationFn: async ({ id, request }) => {
      const response = await dashboardService.updateTemplate(id, request);
      if (response.success) {
        return parseWidgetTemplateResponse(response.value);
      }
      throw restError(response);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [DASHBOARD_KEY, "templates"],
      });
      queryClient.setQueryData([DASHBOARD_KEY, "templates", data.id], data);
    },
  });
};

export const useDeleteWidgetTemplate = () => {
  const dashboardService = useItemService((services) => services.dashboard);
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: [DASHBOARD_KEY, "templates", "delete"],
    mutationFn: async (id) => {
      const response = await dashboardService.deleteTemplate(id);
      if (!response.success) {
        throw restError(response);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DASHBOARD_KEY, "templates"],
      });
    },
  });
};

// ===== Convenience Hook for Saving Dashboard Config =====

export const useSaveDashboardConfig = () => {
  const updateDashboard = useUpdateDashboard();

  return useMutation<
    Dashboard,
    Error,
    { dashboardId: string; name: string; config: DashboardConfig }
  >({
    mutationKey: [DASHBOARD_KEY, "saveConfig"],
    mutationFn: async ({ dashboardId, name, config }) => {
      const configJson = serializeDashboardConfig(config);
      return updateDashboard.mutateAsync({
        id: dashboardId,
        request: { name, configJson },
      });
    },
  });
};
